import { create } from "zustand";
import { persist } from "zustand/middleware";
import { getCachedWorkers } from "./atelierStore";
import { disponibilitesApi } from "@services/api/disponibilites";
import type { AvailabilityBlock, ZoneType } from "./disponibiliteStore";
import { getMonday, getWeekDates } from "./disponibiliteStore";

export type { AvailabilityBlock, ZoneType };
export { getMonday, getWeekDates };

/* ── Zone type config ── */

export const ZONE_COLORS: Record<ZoneType, { bg: string; border: string; text: string; label: string }> = {
  mecanique:      { bg: "#E0E6FF", border: "#CCD0FF", text: "#818CF8", label: "Mécanique" },
  carrosserie:    { bg: "#CACFFF", border: "#B7BDFF", text: "#5C6BFF", label: "Carrosserie" },
  peinture:       { bg: "#FEE2E2", border: "#FECACA", text: "#DC2626", label: "Peinture" },
  diagnostique:   { bg: "#D1FAE5", border: "#A7F3D0", text: "#059669", label: "Diagnostique" },
  service_rapide: { bg: "#D8D4FF", border: "#C5BFFF", text: "#6F61FF", label: "Service rapide" },
  campagnes:      { bg: "#FFEDD5", border: "#FED7AA", text: "#EA580C", label: "Campagnes" },
  administratif:  { bg: "#E2E8F0", border: "#CBD5E1", text: "#475569", label: "Administratif" },
  pause:          { bg: "#DDD2FF", border: "#CDBDFF", text: "#8258FF", label: "Pause" },
  conge:          { bg: "#EFD9FF", border: "#E6C0FF", text: "#CC5EFF", label: "Congé" },
};

// Productive zones first, then the non-productive states (administratif, pause, congé):
// admin time is worked time, but it is NOT workshop capacity — see CAPACITY_ZONES.
export const ZONE_TYPES: ZoneType[] = ["mecanique", "carrosserie", "peinture", "diagnostique", "service_rapide", "campagnes", "administratif", "pause", "conge"];

/** Map old category to zone_type for backward compat */
function inferZoneType(block: AvailabilityBlock): ZoneType {
  if (block.zoneType) return block.zoneType;
  // Legacy: infer from type/category context
  if (block.type === "conge") return "conge";
  return "mecanique";
}

/* ── Store ── */

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

interface DispoAtelierState {
  selectedDate: string;
  viewMode: "jour" | "semaine";
  selectedWorkerId: string;
  selectedWorkerIds: string[];
  blocks: AvailabilityBlock[];
  searchQuery: string;
  /** Zone types to display. Empty array = show all zones. */
  activeZoneFilters: ZoneType[];
  isLoading: boolean;
  _fetchedOrgId: string | undefined;

  fetchBlocks: (orgId: string) => Promise<void>;
  setSelectedDate: (date: string) => void;
  setViewMode: (mode: "jour" | "semaine") => void;
  setSelectedWorkerId: (id: string) => void;
  setSearchQuery: (query: string) => void;
  setActiveZoneFilters: (zones: ZoneType[]) => void;
  toggleWorkerSelection: (id: string) => void;
  /** Select a whole team at once; deselects it when all its members are already in. */
  toggleTeamSelection: (ids: string[]) => void;
  selectAllWorkers: () => void;
  deselectAllWorkers: () => void;

  addBlock: (block: Omit<AvailabilityBlock, "id">, orgId: string) => void;
  updateBlock: (id: string, updates: Partial<Omit<AvailabilityBlock, "id">>) => void;
  removeBlock: (id: string) => void;

  getBlocksForWorkerWeek: (workerId: string, weekStart: string) => AvailabilityBlock[];
  getSummaryByZone: (workerIds: string[]) => { zoneType: ZoneType; label: string; totalHours: number }[];
}

/** Map zoneType to legacy category for backward compat with backend */
function zoneToCategory(zone: ZoneType): "disponibilite" | "service_rapide" {
  return zone === "service_rapide" ? "service_rapide" : "disponibilite";
}

export const useDispoAtelierStore = create<DispoAtelierState>()(
  persist(
    (set, get) => ({
      selectedDate: todayIso(),
      viewMode: "semaine",
      selectedWorkerId: "",
      selectedWorkerIds: [],
      blocks: [],
      searchQuery: "",
      activeZoneFilters: [],
      isLoading: false,
      _fetchedOrgId: undefined,

      fetchBlocks: async (orgId: string) => {
        const state = get();
        if (state.isLoading || state._fetchedOrgId === orgId) return;
        set({ isLoading: true, _fetchedOrgId: orgId });
        try {
          // Fetch ALL blocks (no category filter)
          const blocks = await disponibilitesApi.list({ org_id: orgId });
          // Ensure zoneType is set via inference
          const enriched = blocks.map((b) => ({ ...b, zoneType: inferZoneType(b) }));
          const workers = getCachedWorkers();
          set({
            blocks: enriched,
            isLoading: false,
            // Default to no worker selected ("Personne") — the user opts in to who they want to see.
            selectedWorkerIds: [],
            selectedWorkerId: workers[0]?.id || "",
          });
        } catch (error) {
          console.error("Error fetching dispo atelier blocks:", error);
          set({ blocks: [], isLoading: false, _fetchedOrgId: undefined });
        }
      },

      setSelectedDate: (date) => set({ selectedDate: date }),
      setViewMode: (mode) => set({ viewMode: mode }),
      setSelectedWorkerId: (id) => set({ selectedWorkerId: id }),
      setSearchQuery: (query) => set({ searchQuery: query }),
      setActiveZoneFilters: (zones) => set({ activeZoneFilters: zones }),
      toggleWorkerSelection: (id) =>
        set((state) => ({
          selectedWorkerIds: state.selectedWorkerIds.includes(id)
            ? state.selectedWorkerIds.filter((wId) => wId !== id)
            : [...state.selectedWorkerIds, id],
        })),
      toggleTeamSelection: (ids) =>
        set((state) => {
          const allIn = ids.length > 0 && ids.every((id) => state.selectedWorkerIds.includes(id));
          if (allIn) {
            return { selectedWorkerIds: state.selectedWorkerIds.filter((id) => !ids.includes(id)) };
          }
          // Union without a Set: the CRA production build rejects spreading one (TS2802).
          const merged = state.selectedWorkerIds.slice();
          ids.forEach((id) => {
            if (!merged.includes(id)) merged.push(id);
          });
          return { selectedWorkerIds: merged };
        }),
      selectAllWorkers: () =>
        set({ selectedWorkerIds: getCachedWorkers().map((w) => w.id) }),
      deselectAllWorkers: () => set({ selectedWorkerIds: [] }),

      addBlock: (block, orgId) => {
        if (!block.workerId) return;
        // Unique even when several blocks are added within the same millisecond
        // (e.g. "apply to whole week" creates 5 blocks in one synchronous loop).
        const tempId = `da_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        const zone = block.zoneType || "mecanique";
        set((state) => ({
          blocks: [...state.blocks, { ...block, id: tempId, zoneType: zone }],
        }));
        const worker = getCachedWorkers().find((w) => w.id === block.workerId);
        disponibilitesApi
          .create({ ...block, zoneType: zone }, orgId, zoneToCategory(zone), worker?.name || "")
          .then((created) => {
            set((state) => ({
              blocks: state.blocks.map((b) => (b.id === tempId ? { ...created, zoneType: zone } : b)),
            }));
          })
          .catch((e) => console.error("Error creating block:", e));
      },

      updateBlock: (id, updates) => {
        set((state) => ({
          blocks: state.blocks.map((b) => (b.id === id ? { ...b, ...updates } : b)),
        }));
        disponibilitesApi.update(id, updates).catch((e) => console.error("Error updating block:", e));
      },

      removeBlock: (id) => {
        set((state) => ({
          blocks: state.blocks.filter((b) => b.id !== id),
        }));
        disponibilitesApi.delete(id).catch((e) => console.error("Error deleting block:", e));
      },

      getBlocksForWorkerWeek: (workerId, weekStart) => {
        const { blocks, searchQuery, activeZoneFilters } = get();
        const monday = getMonday(weekStart);
        const weekDates = getWeekDates(monday);

        return blocks.filter((b) => {
          if (b.workerId !== workerId) return false;
          if (activeZoneFilters.length > 0 && (!b.zoneType || !activeZoneFilters.includes(b.zoneType))) return false;
          if (searchQuery && !b.label.toLowerCase().includes(searchQuery.toLowerCase())) return false;
          if (weekDates.includes(b.date)) return true;
          if (b.recurring && b.dayOfWeek !== undefined) return true;
          return false;
        });
      },

      getSummaryByZone: (workerIds: string[]) => {
        const { blocks } = get();
        const map: Record<string, number> = {};
        for (const b of blocks) {
          if (!workerIds.includes(b.workerId)) continue;
          const zone = b.zoneType || "mecanique";
          map[zone] = (map[zone] || 0) + b.durationHours;
        }
        return Object.entries(map).map(([z, hours]) => ({
          zoneType: z as ZoneType,
          label: ZONE_COLORS[z as ZoneType]?.label || z,
          totalHours: hours,
        }));
      },
    }),
    {
      name: "dispo-atelier-prefs",
      partialize: (state) => ({
        viewMode: state.viewMode,
        activeZoneFilters: state.activeZoneFilters,
      }),
    },
  ),
);
